import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function Nav({
  progress,
  homeLink = false,
}: {
  progress: number;
  homeLink?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id: string) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 88;
      const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const logo = (
    <img
      src="https://storage.helloreaddy.io/project_files/fec1b3e3-c832-4504-ae05-19db151b3fe7/ef790dfe-6718-4f0c-ab04-871fe55886ef_compressed_Logo_Vocatur_GmbH.webp"
      alt="Vocatur GmbH"
      className="h-8 w-auto shrink-0"
    />
  );

  const linkClass =
    "text-sm text-inkDim hover:text-ink transition-colors tracking-wide cursor-pointer whitespace-nowrap relative group";

  const crossLinkClass =
    "text-sm text-sage hover:text-ink transition-colors tracking-wide cursor-pointer whitespace-nowrap relative group";

  return (
    <>
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-[2px] z-[60] bg-transparent">
        <div
          className="h-full bg-gradient-to-r from-moss via-sage to-gold transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "py-4 bg-white/95 backdrop-blur-md border-b border-gray-200/50" : "py-6 bg-transparent"
        }`}
      >
        <div className="relative px-6 md:px-12 flex items-center justify-between">
          {homeLink ? (
            <Link
              to="/"
              aria-label="Zurück zur Startseite"
              className="flex items-center gap-2 cursor-pointer group whitespace-nowrap shrink-0"
            >
              {logo}
            </Link>
          ) : (
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-2 cursor-pointer group whitespace-nowrap shrink-0"
            >
              {logo}
            </button>
          )}

          <div className="hidden lg:flex items-center gap-7">
            {!homeLink && (
              <>
                <button onClick={() => handleNav("process")} className={linkClass}>
                  Leistungen
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-moss transition-all duration-300 group-hover:w-full" />
                </button>

                <button onClick={() => handleNav("blog")} className={linkClass}>
                  Blog
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-moss transition-all duration-300 group-hover:w-full" />
                </button>
              </>
            )}

            <Link to="/ki" className={crossLinkClass}>
              KI &amp; Mensch
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-sage transition-all duration-300 group-hover:w-full" />
            </Link>

            <Link to="/karriere" className={crossLinkClass}>
              Karriere
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-sage transition-all duration-300 group-hover:w-full" />
            </Link>

            <Link to="/projekte" className={crossLinkClass}>
              Projekte
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-sage transition-all duration-300 group-hover:w-full" />
            </Link>

            {homeLink && (
              <Link to="/" className={crossLinkClass}>
                Startseite
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-sage transition-all duration-300 group-hover:w-full" />
              </Link>
            )}
          </div>

          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menü"
          >
            <i className={`text-2xl ${open || scrolled ? "text-[#0d1410]" : "text-ink"} ${open ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile dropdown */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            open ? "max-h-[30rem] mt-4" : "max-h-0"
          }`}
        >
          <div className="px-6 pb-6 flex flex-col gap-4 bg-white border-t border-[#e3e2d5] pt-4">
            {!homeLink && (
              <>
                <button
                  onClick={() => handleNav("process")}
                  className="text-left font-display text-xl text-[#0d1410] cursor-pointer whitespace-nowrap"
                >
                  Leistungen
                </button>

                <button
                  onClick={() => handleNav("blog")}
                  className="text-left font-display text-xl text-[#0d1410] cursor-pointer whitespace-nowrap"
                >
                  Blog
                </button>
              </>
            )}

            <Link
              to="/ki"
              onClick={() => setOpen(false)}
              className="text-left font-display text-xl text-[#5d7a42] cursor-pointer whitespace-nowrap"
            >
              KI &amp; Mensch
            </Link>
            <Link
              to="/karriere"
              onClick={() => setOpen(false)}
              className="text-left font-display text-xl text-[#5d7a42] cursor-pointer whitespace-nowrap"
            >
              Karriere
            </Link>
            <Link
              to="/projekte"
              onClick={() => setOpen(false)}
              className="text-left font-display text-xl text-[#5d7a42] cursor-pointer whitespace-nowrap"
            >
              Projekte
            </Link>
            {homeLink && (
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="text-left font-display text-xl text-[#5d7a42] cursor-pointer whitespace-nowrap"
              >
                Startseite
              </Link>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}